// One conversation: messages, composer, statuses, contact details (safety number, devices).

import { getConversations } from '../space/current';
import { safetyNumber } from '../contacts/identity';
import { deviceCode } from '../space/link';
import type { Conversation, DeliveryStatus, Message, Notice } from '../contacts/conversation';
import type { Conversations } from '../contacts/conversations';
import { h, keep } from './dom';
import { contactLastSeen, contactOnline, contactPicker, personLastSeen, personOnline } from './chats';
import { relativeDate } from './notes';
import type { View } from './view';

const STATUS: Record<DeliveryStatus, string> = { pending: 'en attente', delivered: 'distribué', read: 'lu' };
const QUICK = ['👍', '❤️', '😂'];

/** Direct: pending / delivered / read. Group: counted per person. */
function statusText(c: Conversation, m: Message): string {
  if (c.kind !== 'group') return STATUS[c.status(m)];
  const r = c.receipts(m);
  if (!r.total) return 'envoyé';
  return r.read ? `distribué ${r.delivered}/${r.total} · lu ${r.read}/${r.total}` : r.delivered ? `distribué ${r.delivered}/${r.total}` : 'en attente';
}

export function chatView(root: HTMLElement, id: string): View {
  let conv: Conversation | null = null;
  let manager: Conversations | null = null;
  let safety = '';
  let editing: string | null = null;
  let frame = 0;
  let stickToBottom = true;
  let detailsSig = '';
  const timer = setInterval(() => schedule(), 30_000);

  const list = h('div', { class: 'messages', role: 'log', 'aria-live': 'polite' });
  const input = h('textarea', { class: 'composer', rows: 1, placeholder: 'Message', 'aria-label': 'Message' });
  const send = h('button', { class: 'btn primary small', text: 'Envoyer', onclick: () => void submit() });
  const header = h('div', { class: 'row chat-head' });
  const details = h('details', { class: 'card small' });
  const notice = h('p', { class: 'dim small' });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !matchMedia('(pointer: coarse)').matches) {
      e.preventDefault();
      void submit();
    }
    if (e.key === 'Escape' && editing) ((editing = null), (input.value = ''), render());
  });
  input.addEventListener('input', () => {
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 160)}px`;
  });
  list.addEventListener('scroll', () => (stickToBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 40));

  function schedule() {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(render);
  }

  async function submit() {
    const text = input.value.trim();
    if (!conv || !text) return;
    if (editing) await conv.edit(editing, text);
    else await conv.post(text);
    editing = null;
    input.value = '';
    input.style.height = 'auto';
    stickToBottom = true;
    render();
  }

  function bubble(m: Message): HTMLElement {
    const c = conv!;
    const mine = c.isMine(m);
    const reactions = [...m.reactions].map(([emoji, who]) => h('span', { class: 'reaction', text: `${emoji}${who.size > 1 ? ` ${who.size}` : ''}` }));
    const actions = !m.deleted
      ? h(
          'div',
          { class: 'msg-actions' },
          ...QUICK.map((emoji) => h('button', { class: 'icon-btn small', 'aria-label': `Réagir ${emoji}`, text: emoji, onclick: () => void c.react(m.id, emoji, !m.reactions.get(emoji)?.has(c.me)) })),
          mine && h('button', { class: 'icon-btn small', text: 'modifier', onclick: () => ((editing = m.id), (input.value = m.text), input.focus(), render()) }),
          mine && h('button', { class: 'icon-btn small', text: 'supprimer', onclick: () => confirm('Supprimer ce message pour tout le monde ?') && void c.remove(m.id) }),
        )
      : null;
    return h(
      'div',
      { class: `msg ${mine ? 'mine' : 'theirs'}${m.deleted ? ' deleted' : ''}` },
      c.kind === 'group' && !mine && h('div', { class: 'msg-author', text: c.nameOf(m.person) }),
      h('div', { class: 'msg-text', text: m.deleted ? 'message supprimé' : m.text }),
      reactions.length ? h('div', { class: 'reactions' }, ...reactions) : null,
      h('div', { class: 'msg-meta dim' }, new Date(m.ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }), m.edited && !m.deleted ? ' · modifié' : '', mine && !m.deleted ? ` · ${statusText(c, m)}` : ''),
      actions,
    );
  }

  function noticeLine(c: Conversation, n: Notice): HTMLElement {
    // "Tu as…" for me, "Alice a…" for others.
    const did = (p: string | null, verb: string) => (p === c.me ? `Tu as ${verb}` : `${c.nameOf(p)} a ${verb}`);
    const text =
      n.kind === 'added' ? did(n.actor, `ajouté ${c.nameOf(n.target)}`)
      : n.kind === 'left' ? `${c.nameOf(n.target)} a quitté le groupe`
      : n.kind === 'removed' ? did(n.actor, `retiré ${c.nameOf(n.target)}`)
      : did(n.actor, `renommé le groupe « ${n.name} »`);
    return h('p', { class: 'notice dim small', text });
  }

  function renderHeader(c: Conversation) {
    if (c.kind === 'group') {
      const online = c.others().filter((p) => personOnline(c, p)).length;
      header.replaceChildren(
        h('a', { class: 'btn small', href: '#/chats', text: '←' }),
        h('span', { class: 'chat-title' }, h('strong', { text: c.title() }), h('span', { class: online ? 'ok small' : 'dim small', text: ` ${c.persons().length} membres${online ? ` · ${online} en ligne` : ''}` })),
      );
      return;
    }
    const online = contactOnline(c);
    const seen = contactLastSeen(c);
    header.replaceChildren(
      h('a', { class: 'btn small', href: '#/chats', text: '←' }),
      h('span', { class: 'chat-title' }, h('strong', { text: c.peerName() }), h('span', { class: online ? 'ok small' : 'dim small', text: online ? ' en ligne' : seen ? ` vu ${relativeDate(seen)}` : ' jamais vu en ligne' })),
    );
  }

  function renderGroupDetails(c: Conversation) {
    const amCreator = c.creator === c.me;
    const members = c.persons().sort((a, b) => (a === c.me ? -1 : b === c.me ? 1 : c.nameOf(a).localeCompare(c.nameOf(b))));
    let picker: ReturnType<typeof contactPicker> | null = null;
    const addBox = h('div', { class: 'stack' });
    void getConversations().then((cs) => {
      if (!cs) return;
      picker = contactPicker(cs, new Set(c.persons()));
      addBox.replaceChildren(
        ...keep(
        h('h2', { text: 'Ajouter des contacts' }),
        picker.el,
        picker.count > 0 &&
        h('button', {
          class: 'btn small',
          text: 'Ajouter',
          onclick: async () => {
            const people = picker!.picked();
            if (people.length) await cs.addToGroup(c, people);
            render();
          },
        }),
        ),
      );
    });
    details.replaceChildren(
      ...keep(
        h('summary', { text: 'Membres et réglages' }),
        h(
          'ul',
          { class: 'files' },
          ...members.map((p) => {
            const seen = personLastSeen(c, p);
            return h(
              'li',
              { class: 'device' },
              h('div', { class: 'file-head' }, h('span', { class: 'file-name', text: `${c.nameOf(p)}${p === c.me ? ' (toi)' : ''}${p === c.creator ? ' · administrateur' : ''}` }), p !== c.me && h('span', { class: personOnline(c, p) ? 'ok small' : 'dim small', text: personOnline(c, p) ? 'en ligne' : seen ? `vu ${relativeDate(seen)}` : 'jamais vu' })),
              amCreator && p !== c.me && h('button', { class: 'btn small', text: 'Retirer du groupe', onclick: () => confirm(`Retirer ${c.nameOf(p)} du groupe ? Il ne lira plus les nouveaux messages.`) && void getConversations().then((cs) => cs?.removeFromGroup(c, p)) }),
            );
          }),
        ),
        addBox,
        amCreator && h('button', { class: 'btn small', text: 'Renommer le groupe', onclick: () => { const n = prompt('Nom du groupe :', c.title()); if (n?.trim()) void c.rename(n); } }),
        h('button', {
          class: 'btn danger small',
          text: 'Quitter le groupe',
          onclick: async () => {
            if (!confirm('Quitter ce groupe ? Tu ne recevras plus ses messages, sur aucun de tes appareils.')) return;
            await (await getConversations())?.leaveGroup(c);
            location.hash = '#/chats';
          },
        }),
      ),
    );
  }

  function renderDetails(c: Conversation) {
    if (c.kind === 'group' || !c.peer) return renderGroupDetails(c);
    const peer = c.peer;
    const pending = c.list().filter((m) => c.isMine(m) && !m.deleted && c.status(m) === 'pending');
    const oldest = pending[0];
    const theirs = c.devicesOf(peer.identity);
    const notify =
      'Notification' in window && Notification.permission !== 'granted'
        ? h('button', { class: 'btn small', text: 'Notifications (app ouverte)', onclick: () => void Notification.requestPermission().then(render) })
        : null;
    details.replaceChildren(
      ...keep(
        h('summary', { text: 'Détails' }),
        h('p', { class: 'dim', text: 'Numéro de sécurité (identique chez ton contact) :' }),
        h('p', { class: 'safety', text: safety }),
        h('p', { class: 'dim', text: `Nom choisi par ton contact : ${c.profiles.get(peer.identity)?.name ?? peer.name}` }),
        h('button', {
          class: 'btn small',
          text: c.aliasFor(peer.identity) ? 'Changer le surnom (visible par toi seul)' : 'Renommer ce contact (visible par toi seul)',
          onclick: () => {
            const n = prompt('Surnom de ce contact, visible par toi seul (vide pour revenir à son nom) :', c.aliasFor(peer.identity) ?? '');
            if (n !== null) void getConversations().then((cs) => cs?.setAlias(peer.identity, n || null));
          },
        }),
        h('p', { class: 'dim', text: `Appareils de ${c.peerName()} :` }),
        h('ul', { class: 'compact' }, ...theirs.map((d) => h('li', { class: 'dim', text: `${c.journal.members.get(d)?.record.name ?? 'appareil'} (${deviceCode(d)}) · ${c.sync?.lastSeen.get(d) ? `vu ${relativeDate(c.sync.lastSeen.get(d)!)}` : 'jamais vu'}` }))),
        h('p', { class: 'dim', text: pending.length ? `Limite du pair à pair : ${pending.length} message${pending.length > 1 ? 's' : ''} en attente, le plus ancien depuis ${relativeDate(oldest.ts).replace('il y a ', '')}.` : 'Aucun message en attente.' }),
        notify,
      ),
    );
  }

  function render() {
    const c = conv;
    if (!c) return;
    renderHeader(c);
    // Only when what it shows changed: redrawing on every message would reset checkboxes being ticked.
    const sig = JSON.stringify([c.persons().map((p) => [p, c.nameOf(p), c.kind === 'group' ? personOnline(c, p) : 0]), c.title(), c.list().filter((m) => c.isMine(m) && c.status(m) === 'pending').length]);
    if (sig !== detailsSig || !details.childElementCount) {
      detailsSig = sig;
      renderDetails(c);
    }
    const items = c.timeline();
    list.replaceChildren(...(items.length ? items.map((x) => ('id' in x ? bubble(x) : noticeLine(c, x))) : [h('p', { class: 'dim', text: 'Écris le premier message.' })]));
    if (stickToBottom) list.scrollTop = list.scrollHeight;
    const member = c.journal.isMember;
    input.disabled = send.disabled = !member;
    notice.textContent = !member ? 'Synchronisation de la discussion…' : editing ? 'Modification du message (Échap pour annuler)' : '';
    send.textContent = editing ? 'Modifier' : 'Envoyer';
    if (document.visibilityState === 'visible' && member) void c.markRead();
  }

  let lastCount = 0;
  const onChange = () => {
    const c = conv!;
    const count = c.list().length;
    // A local notification for new messages while the app is open but not looked at.
    if (count > lastCount && document.visibilityState === 'hidden' && 'Notification' in window && Notification.permission === 'granted') {
      const m = c.list().at(-1);
      if (m && !c.isMine(m)) new Notification(c.kind === 'group' ? `${c.nameOf(m.person)} · ${c.title()}` : c.peerName(), { body: m.text.slice(0, 140), tag: c.id });
    }
    lastCount = count;
    schedule();
  };
  const onVisible = () => document.visibilityState === 'visible' && schedule();

  root.replaceChildren(h('p', { class: 'dim', text: 'Ouverture…' }));
  void getConversations().then(async (convs) => {
    conv = convs?.map.get(id) ?? null;
    if (!conv) return void (location.hash = '#/chats');
    manager = convs;
    manager!.addEventListener('change', schedule); // names and nicknames
    if (conv.peer) safety = await safetyNumber(conv.me, conv.peer.identity);
    lastCount = conv.list().length;
    conv.addEventListener('change', onChange);
    conv.sync?.addEventListener('peers', schedule);
    conv.sync?.addEventListener('known', schedule);
    document.addEventListener('visibilitychange', onVisible);
    root.replaceChildren(header, details, list, notice, h('div', { class: 'row composer-row' }, input, send));
    render();
  });

  return {
    destroy() {
      cancelAnimationFrame(frame);
      clearInterval(timer);
      conv?.removeEventListener('change', onChange);
      manager?.removeEventListener('change', schedule);
      conv?.sync?.removeEventListener('peers', schedule);
      conv?.sync?.removeEventListener('known', schedule);
      document.removeEventListener('visibilitychange', onVisible);
    },
  };
}
